import React, { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, X, GripVertical, Archive, EyeOff, Eye } from 'lucide-react';


const Properties = ({ properties = [], onUpdateProperties, onDeleteProperty, reservations = [] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [draggedProperty, setDraggedProperty] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    hospitableId: '',
    cleaningFee: ''
  });

  // Filter properties based on archive state (hidden calendar = archived)
  const visibleProperties = useMemo(() => {
    const sorted = [...properties].sort((a, b) => a.order - b.order);
    if (showArchived) return sorted;
    return sorted.filter(p => !p.hidden);
  }, [properties, showArchived]);

  // Count of archived (hidden) properties
  const archivedCount = useMemo(() => {
    return properties.filter(p => p.hidden).length;
  }, [properties]);

  const handleDragStart = (property) => {
    setDraggedProperty(property);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (targetProperty) => {
    if (!draggedProperty || draggedProperty._id === targetProperty._id) return;

    const draggedIndex = properties.findIndex(p => p._id === draggedProperty._id);
    const targetIndex = properties.findIndex(p => p._id === targetProperty._id);

    // Create new array without mutating the original
    const updatedProperties = properties.filter(p => p._id !== draggedProperty._id);

    // Calculate the correct insertion index
    // If dragging down, the target index stays the same after removal
    // If dragging up, we need to insert at the original target position
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;

    // Insert at the correct position
    updatedProperties.splice(insertIndex, 0, draggedProperty);

    const reorderedProperties = updatedProperties.map((prop, index) => ({
      ...prop,
      order: index
    }));

    onUpdateProperties(reorderedProperties);
    setDraggedProperty(null);
  };

  const handleAddProperty = () => {
    setCurrentProperty(null);
    setFormData({
      name: '',
      hospitableId: '',
      cleaningFee: ''
    });
    setIsModalOpen(true);
  };

  const handleEditProperty = (property) => {
    setCurrentProperty(property);
    setFormData({
      name: property.name,
      hospitableId: property.hospitableId || '',
      cleaningFee: property.cleaningFee || ''
    });
    setIsModalOpen(true);
  };

  const handleToggleHidden = (property) => {
    const updatedProperties = properties.map(p =>
      p._id === property._id ? { ...p, hidden: !p.hidden } : p
    );
    onUpdateProperties(updatedProperties);
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    await onDeleteProperty(propertyId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentProperty) {
      const updatedProperties = properties.map(p =>
        p._id === currentProperty._id
          ? { ...currentProperty, ...formData }
          : p
      );
      onUpdateProperties(updatedProperties);
    } else {
      const newProperty = {
        ...formData,
        order: properties.length
      };
      onUpdateProperties([...properties, newProperty]);
    }
    
    setIsModalOpen(false);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col bg-zinc-900 min-h-full">
        <div className="border-b border-zinc-800 p-4 flex justify-end items-center gap-2">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors border ${
                showArchived
                  ? 'bg-amber-600/20 text-amber-400 border-amber-700 hover:bg-amber-600/30'
                  : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700'
              }`}
            >
              <Archive className="w-4 h-4 mr-1" />
              {showArchived ? 'Hide' : 'Show'} Archived ({archivedCount})
            </button>
          )}
          <button
            onClick={handleAddProperty}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Property
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-950">
              <tr>
                <th scope="col" className="w-10"></th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Property Name
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Hospitable ID
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Cleaning Fee
                </th>
                <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Calendar
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-zinc-900 divide-y divide-zinc-800">
              {visibleProperties.map((property) => (
                <tr
                  key={property._id}
                  draggable
                  onDragStart={() => handleDragStart(property)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(property)}
                  className="hover:bg-zinc-800/50 transition-colors"
                >
                  <td className="px-2">
                    <GripVertical className="w-4 h-4 text-zinc-600 cursor-move" />
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-zinc-200">
                    {property.name}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-zinc-500">
                    {property.hospitableId || '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-zinc-500">
                    {property.cleaningFee ? `$${property.cleaningFee}` : '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-center text-sm">
                    <button
                      onClick={() => handleToggleHidden(property)}
                      className={`transition-colors ${property.hidden ? 'text-zinc-600 hover:text-zinc-400' : 'text-emerald-500 hover:text-emerald-400'}`}
                      title={property.hidden ? 'Hidden from calendar - click to show' : 'Visible on calendar - click to hide'}
                    >
                      {property.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEditProperty(property)}
                        className="text-zinc-500 hover:text-violet-400 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProperty(property._id)}
                        className="text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-zinc-800 flex justify-between items-center">
              <h3 className="text-base font-semibold text-zinc-100">
                {currentProperty ? 'Edit Property' : 'Add Property'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Property Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="hospitableId" className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Hospitable ID
                  </label>
                  <input
                    type="text"
                    name="hospitableId"
                    id="hospitableId"
                    value={formData.hospitableId}
                    onChange={(e) => setFormData({ ...formData, hospitableId: e.target.value })}
                    className="block w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="cleaningFee" className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Cleaning Fee
                  </label>
                  <input
                    type="number"
                    name="cleaningFee"
                    id="cleaningFee"
                    value={formData.cleaningFee}
                    onChange={(e) => setFormData({ ...formData, cleaningFee: e.target.value })}
                    className="block w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border border-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 transition-colors"
                >
                  {currentProperty ? 'Save Changes' : 'Add Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Properties;